import { useState, useRef, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useQuery } from '@tanstack/react-query';
import { posApi } from '@/services/api';
import { getInvoiceDetailsRouted } from '@/offline/posModuleRouter';
import { getCachedSettings, isLocalFirstMode } from '@/offline';
import { useAuthStore } from '@/store/authStore';
import { printEscPosBytes, isThermalPrinterConfigured } from '@/hooks/usePrinterConnection';
import { usePrinterStore } from '@/store/printerStore';
import { receiptToDantsu } from '@/utils/receiptToDantsu';
import { convertDantsuToEscPos } from '@/utils/thermalPrinterUtils';
import { X, Printer, Download, Check, Loader2, AlertTriangle, Share2 } from 'lucide-react';
import type { ReceiptData, PosSettings } from '@/types';
import { resolveFileUrl } from '@/utils/fileUrl';
import { formatMoney } from '@/utils/currency';
import { normalizeLayoutMode, parseReceiptTemplateSettings } from '@/utils/receiptTemplate';
import { captureReceiptPng, downloadDataUrl, shareReceiptImage } from '@/utils/shareReceiptImage';
import {
  companyContactParts,
  FiscalBlock,
  ZatcaBlock,
  PricesIncludeTaxNote,
  SellerIdsBlock,
} from '@/components/pos/receiptLayouts/blocks';
import { getDisplayCompanyName } from '@/utils/receiptCompliance';
import { ModernReceiptLayout, ShortReceiptLayout } from '@/components/pos/receiptLayouts/ShortModern';
import ReceiptPromoPrice from '@/components/pos/ReceiptPromoPrice';
import { receiptPromoTotals, showReceiptDiscountPrint } from '@/utils/receiptPromo';
import { getPoweredByI18nKey } from '@/utils/themeDomain';

interface Props {
  invoiceName?: string;
  onClose?: () => void;
  previewInvoice?: any;
  previewSettings?: any;
  /** Optional company/locale override for designer preview */
  previewPosSettings?: PosSettings | null;
}


export default function Receipt({ invoiceName, onClose = () => {}, previewInvoice, previewSettings, previewPosSettings }: Props) {
  const { t } = useTranslation();
  const poweredBy = t(getPoweredByI18nKey());
  const { settings } = usePrinterStore();
  const [showSuccess, setShowSuccess] = useState(true);
  const [printing, setPrinting] = useState(false);
  const [printError, setPrintError] = useState('');
  const [sharing, setSharing] = useState(false);
  const visualReceiptRef = useRef<HTMLDivElement>(null);
  const { company } = useAuthStore();

  /** Translate stock English thank-you footers; keep custom footer text as-is. */
  const resolveFooter = (footer?: string | null) => {
    const raw = (footer || '').trim();
    if (!raw) return '';
    const normalized = raw.replace(/!$/, '').trim().toLowerCase();
    if (normalized === 'thank you' || normalized === 'thanks') {
      return t('receipt.thankYou');
    }
    return raw;
  };

  useEffect(() => {
    const timer = setTimeout(() => setShowSuccess(false), 2000);
    return () => clearTimeout(timer);
  }, []);

  
  const { data: invoiceData, isLoading } = useQuery({
    queryKey: ['invoiceDetails', invoiceName],
    queryFn: async () => {
      if (!invoiceName) return null;
      // Offline/local invoices (OFF-…) live in IndexedDB until synced — never hit server.
      return (await getInvoiceDetailsRouted(invoiceName)) as ReceiptData;
    },
    enabled: !previewInvoice && !!invoiceName,
    retry: false,
  });

  const { data: fetchedPosSettings } = useQuery({
    queryKey: ['posSettings', 'receipt'],
    queryFn: async () => {
      if (isLocalFirstMode()) {
        const cached = await getCachedSettings<PosSettings>();
        if (cached) return cached;
      }
      try {
        const res = await posApi.getPosSettings();
        return res.data.message as PosSettings;
      } catch {
        return ((await getCachedSettings<PosSettings>()) || {}) as PosSettings;
      }
    },
    enabled: !previewPosSettings,
  });

  const posSettings = previewPosSettings || fetchedPosSettings;




  const isPrinterConfigured = isThermalPrinterConfigured(settings);

  const inv = previewInvoice || invoiceData;
  const displayCompany = getDisplayCompanyName(inv, posSettings) || company?.name || 'Sale Desk';
  const servedBy = inv?.cashier_name || inv?.cashier;

  const tmplSettings = previewSettings
    ? parseReceiptTemplateSettings(previewSettings)
    : parseReceiptTemplateSettings(posSettings?.receipt_template_settings);

  const layoutMode = normalizeLayoutMode(tmplSettings.layout_mode);
  const taxLabel = ((posSettings?.tax_label || 'Tax').trim() || 'Tax');
  const showPromoPrint = showReceiptDiscountPrint(posSettings);
  const promoTotals = receiptPromoTotals(inv);
  const showPromoRow = showPromoPrint && promoTotals.promo > 0;
  const subtotalDisplay = showPromoRow ? promoTotals.original : promoTotals.charged;
  const contactJoin = (sep: string) =>
    companyContactParts(inv, tmplSettings).join(sep);

  const handlePrint = async () => {
    const printInv = previewInvoice || invoiceData;
    if (!printInv) return;
    setPrintError('');

    if (!isPrinterConfigured) {
      window.print();
      return;
    }

    setPrinting(true);
    try {
      const dantsuString = receiptToDantsu(
        printInv,
        {
          companyName: printInv.company_name || company?.name,
          paperWidth: settings.paperWidth === 58 ? '58mm' : '80mm',
          posSettings
        }
      );
      const escposData = await convertDantsuToEscPos(dantsuString, settings.feedLines, settings.autoCut);
      await printEscPosBytes(escposData, { allowPicker: true, settings });
    } catch (err: any) {
      const msg = err?.message || t('receipt.directPrintFailed');
      setPrintError(msg);
    } finally {
      setPrinting(false);
    }
  };

  // Approved: download keeps fancy visual receipt as PNG
  const handleDownloadImage = async () => {
    if (!visualReceiptRef.current) return;
    const width = layoutMode === 'modern' || layoutMode === 'short' || layoutMode === 'compact' ? 420 : 680;
    try {
      const { dataUrl, fileName } = await captureReceiptPng(visualReceiptRef.current, {
        width,
        fileName: `Receipt-${invoiceName || 'invoice'}.png`,
      });
      downloadDataUrl(dataUrl, fileName);
    } catch (err) {
      console.error('Failed to generate image', err);
      alert(t('receipt.failedImage'));
    }
  };

  const handleShareWhatsApp = async () => {
    if (!visualReceiptRef.current || !inv) return;
    const width = layoutMode === 'modern' || layoutMode === 'short' || layoutMode === 'compact' ? 420 : 680;
    setSharing(true);
    try {
      await shareReceiptImage({
        source: visualReceiptRef.current,
        invoiceName: inv.name || invoiceName,
        totalLabel: formatMoney(inv.net_total),
        customerMobile: (inv as any).customer_mobile || (inv as any).mobile,
        width,
      });
    } catch (err) {
      console.error('Share failed', err);
      alert(t('receipt.failedShare'));
    } finally {
      setSharing(false);
    }
  };

  const renderReceipt = (ref: any) => (
    <div ref={ref} className="invoice-wrapper w-full">
      <style>{`
        .invoice-wrapper * { box-sizing: border-box; }
        .invoice-wrapper {
          --font-scale: ${tmplSettings.font_size === 'small' ? 0.85 : tmplSettings.font_size === 'large' ? 1.2 : 1};
          font-family: 'Segoe UI', Arial, sans-serif;
          background: #eef1f5;
          margin: 0;
          padding: 20px 0;
          width: 100%;
        }
        .invoice-wrapper .invoice {
          max-width: 680px;
          margin: 0 auto;
          background: #fff;
          border-radius: 6px;
          overflow: hidden;
          box-shadow: 0 2px 10px rgba(0,0,0,0.08);
          ${tmplSettings.font_size === 'small' ? 'font-size: calc(12px * var(--font-scale)) !important;' : tmplSettings.font_size === 'large' ? 'font-size: calc(17px * var(--font-scale)) !important;' : ''}
        }

        /* Layout Standard Styles */
        .invoice-wrapper .invoice.layout-standard {
          padding: 30px 40px;
        }
        .invoice-wrapper .invoice.layout-standard .top-bar { height: 8px; background: #1c4fc4; margin: -30px -40px 20px -40px; }
        .invoice-wrapper .invoice.layout-standard .header { display: flex; justify-content: space-between; align-items: flex-start; padding: 10px 0 20px 0; }
        .invoice-wrapper .invoice.layout-standard .logo-row { display: flex; align-items: center; gap: 14px; }
        .invoice-wrapper .invoice.layout-standard .logo-img { max-width: ${tmplSettings.logo_width}px !important; max-height: 70px; flex-shrink: 0; object-fit: contain; }
        .invoice-wrapper .invoice.layout-standard .brand-name { font-size: calc(30px * var(--font-scale)); font-weight: 800; color: #14213d; line-height: 1.1; text-transform: none; }
        .invoice-wrapper .invoice.layout-standard .brand-name span { color: #1c4fc4; }
        .invoice-wrapper .invoice.layout-standard .contact-info { text-align: left; font-size: calc(13.5px * var(--font-scale)); color: #333; }
        .invoice-wrapper .invoice.layout-standard .contact-info div { display: flex; align-items: center; gap: 8px; margin-bottom: 10px; white-space: nowrap; }
        .invoice-wrapper .invoice.layout-standard .contact-info .icon { width: 20px; height: 20px; color: #1c4fc4; flex-shrink: 0; }
        .invoice-wrapper .invoice.layout-standard .divider { border: none; border-top: 1px dashed #c9d0da; margin: 10px 0; }
        .invoice-wrapper .invoice.layout-standard .meta-container { background: #f8fafc; border: 1px solid #e2e8f0; border-radius: 8px; padding: 16px; margin: 16px 0; }
        .invoice-wrapper .invoice.layout-standard .meta-row { display: flex; align-items: center; gap: 14px; margin-bottom: 12px; font-size: calc(14.5px * var(--font-scale)); }
        .invoice-wrapper .invoice.layout-standard .meta-row:last-child { margin-bottom: 0; }
        .invoice-wrapper .invoice.layout-standard .meta-icon { width: 30px; height: 30px; background: #1c4fc4; border-radius: 6px; display: flex; align-items: center; justify-content: center; color: #fff; flex-shrink: 0; }
        .invoice-wrapper .invoice.layout-standard .meta-icon svg { width: 16px; height: 16px; }
        .invoice-wrapper .invoice.layout-standard .meta-label { font-weight: 700; color: #14213d; width: 110px; }
        .invoice-wrapper .invoice.layout-standard .meta-sep { color: #14213d; margin-right: 8px; }
        .invoice-wrapper .invoice.layout-standard .meta-value { color: #333; }
        .invoice-wrapper .invoice.layout-standard table.items { width: 100%; border-collapse: collapse; border: 1px solid #e3e7ee; border-radius: 6px; overflow: hidden; margin-top: 16px; }
        .invoice-wrapper .invoice.layout-standard table.items thead th { background: #1c4fc4; color: #fff; text-align: left; padding: 14px 16px; font-size: calc(14px * var(--font-scale)); }
        .invoice-wrapper .invoice.layout-standard table.items thead th.num-col { width: 50px; text-align: center; }
        .invoice-wrapper .invoice.layout-standard table.items thead th.right { text-align: right; }
        .invoice-wrapper .invoice.layout-standard table.items tbody td { padding: 16px; font-size: calc(14.5px * var(--font-scale)); color: #222; border-bottom: 1px dashed #e3e7ee; }
        .invoice-wrapper .invoice.layout-standard table.items tbody tr:last-child td { border-bottom: none; }
        .invoice-wrapper .invoice.layout-standard table.items tbody td.num-col { text-align: center; font-weight: 700; color: #14213d; }
        .invoice-wrapper .invoice.layout-standard table.items tbody td.right { text-align: right; }
        .invoice-wrapper .invoice.layout-standard table.items tbody td.amount { font-weight: 700; }
        .invoice-wrapper .invoice.layout-standard .totals { width: 300px; margin: 24px 0 0 auto; }
        .invoice-wrapper .invoice.layout-standard .totals-row { display: flex; justify-content: space-between; padding: 10px 4px; font-size: calc(14.5px * var(--font-scale)); color: #333; border-bottom: 1px dashed #e3e7ee; }
        .invoice-wrapper .invoice.layout-standard .totals-highlight { font-weight: 800; color: #1c4fc4; font-size: calc(17px * var(--font-scale)); border-bottom: none; }
        .invoice-wrapper .invoice.layout-standard .paid-box { background: #1c4fc4; color: #fff; display: flex; justify-content: space-between; padding: 14px 16px; font-weight: 700; font-size: calc(15px * var(--font-scale)); border-radius: 4px 4px 0 0; margin-top: 6px; }
        .invoice-wrapper .invoice.layout-standard .status-box { background: #f2f7f2; color: #1e8a3c; display: flex; align-items: center; justify-content: center; gap: 8px; width: 100%; padding: 12px 16px; font-weight: 700; font-size: calc(14px * var(--font-scale)); border-radius: 0 0 4px 4px; text-align: center; }
        .invoice-wrapper .invoice.layout-standard .status-box.unpaid { background: #fef3c7; color: #d97706; }
        .invoice-wrapper .invoice.layout-standard .status-box.unpaid .check { background: #d97706; }
        .invoice-wrapper .invoice.layout-standard .status-box .check { width: 18px; height: 18px; background: #1e8a3c; color: #fff; border-radius: 50%; display: flex; align-items: center; justify-content: center; font-size: calc(12px * var(--font-scale)); }
        .invoice-wrapper .invoice.layout-standard .footer { text-align: center; padding: 34px 0 10px 0; }
        .invoice-wrapper .invoice.layout-standard .footer .thanks { font-size: calc(15px * var(--font-scale)); color: #14213d; font-weight: 600; margin-bottom: 8px; }
        .invoice-wrapper .invoice.layout-standard .footer .thanks .star { color: #1c4fc4; }
        .invoice-wrapper .invoice.layout-standard .footer .sub { font-size: calc(13.5px * var(--font-scale)); color: #5a6472; margin-bottom: 22px; }
        .invoice-wrapper .invoice.layout-standard .footer-line { border: none; border-top: 1px solid #1c4fc4; margin: 0 0 16px 0; }
        .invoice-wrapper .invoice.layout-standard .powered { font-size: calc(13.5px * var(--font-scale)); color: #333; }
        .invoice-wrapper .invoice.layout-standard .powered b { color: #1c4fc4; }
        .invoice-wrapper .invoice.layout-standard .powered .tag { font-size: calc(12.5px * var(--font-scale)); color: #5a6472; margin-top: 4px; }
        .invoice-wrapper .invoice.layout-standard .item-card { border: 1px solid #e3e7ee; border-radius: 6px; padding: 12px; margin-bottom: 10px; background: #fafbfc; }
        .invoice-wrapper .invoice.layout-standard .item-card-title { font-size: calc(14px * var(--font-scale)); font-weight: 700; color: #14213d; margin-bottom: 8px; line-height: 1.35; }
        .invoice-wrapper .invoice.layout-standard .item-card-row { display: flex; justify-content: space-between; gap: 12px; font-size: calc(13px * var(--font-scale)); color: #333; margin-top: 4px; }

        /* Layout Compact Styles */
        .invoice-wrapper .invoice.layout-compact {
          font-family: monospace, Courier, monospace !important;
          padding: 20px 24px;
          line-height: 1.3 !important;
        }
        .invoice-wrapper .invoice.layout-compact .logo-img { max-width: ${Math.min(tmplSettings.logo_width || 120, 95)}px !important; max-height: 50px; object-fit: contain; }
        .invoice-wrapper .invoice.layout-compact .divider-dashed { border-top: 1px dashed #64748b; margin: 12px 0; }
        .invoice-wrapper .invoice.layout-compact .divider-dotted { border-top: 1px dotted #cbd5e1; margin: 8px 0; }
        .invoice-wrapper .invoice.layout-compact .compact-meta-row { display: grid; grid-template-columns: 1fr 1fr; gap: 6px 16px; font-size: calc(13px * var(--font-scale)); margin-bottom: 8px; }
        .invoice-wrapper .invoice.layout-compact table.items { width: 100%; border-collapse: collapse; margin-top: 10px; }
        .invoice-wrapper .invoice.layout-compact table.items th { border-bottom: 1px dashed #64748b; background: transparent; color: #1e293b; padding: 6px 4px; font-size: calc(13px * var(--font-scale)); font-weight: bold; }
        .invoice-wrapper .invoice.layout-compact table.items td { border-bottom: 1px dotted #e2e8f0; padding: 8px 4px; font-size: calc(13px * var(--font-scale)); }
        .invoice-wrapper .invoice.layout-compact .totals { width: 100%; margin-top: 12px; }
        .invoice-wrapper .invoice.layout-compact .totals-row { display: flex; justify-content: space-between; font-size: calc(13px * var(--font-scale)); border-bottom: 1px dotted #cbd5e1; padding: 6px 0; color: #1e293b; }
        .invoice-wrapper .invoice.layout-compact .totals-row.total { font-weight: bold; font-size: calc(14.5px * var(--font-scale)); border-bottom: 1px dashed #64748b; padding-bottom: 8px; }
        .invoice-wrapper .invoice.layout-compact .footer { text-align: center; padding-top: 16px; font-size: calc(13px * var(--font-scale)); }

        
        
        /* Layout Elegant Styles */
        .invoice-wrapper .invoice.layout-elegant {
          font-family: Georgia, serif !important;
          padding: 45px 50px;
          line-height: 1.65 !important;
        }
        .invoice-wrapper .invoice.layout-elegant .logo-img { max-width: ${tmplSettings.logo_width}px !important; max-height: 80px; object-fit: contain; }
        .invoice-wrapper .invoice.layout-elegant .elegant-title { font-family: Georgia, serif; font-style: italic; font-weight: bold; font-size: calc(28px * var(--font-scale)); color: #0f172a; text-transform: none; text-align: center; letter-spacing: 0.025em; }
        .invoice-wrapper .invoice.layout-elegant .elegant-line { width: 48px; height: 1px; background: #d97706; margin: 8px auto; }
        .invoice-wrapper .invoice.layout-elegant .divider-elegant-double { border-top: 2px solid #fde68a; margin: 20px 0; }
        .invoice-wrapper .invoice.layout-elegant .divider-elegant-single { border-top: 1px solid #fde68a; margin: 16px 0; }
        .invoice-wrapper .invoice.layout-elegant .meta-section { display: grid; grid-template-columns: 1fr 1fr; gap: 24px; font-size: calc(14.5px * var(--font-scale)); margin-bottom: 24px; color: #475569; }
        .invoice-wrapper .invoice.layout-elegant .meta-section strong { color: #1e293b; font-weight: 700; }
        .invoice-wrapper .invoice.layout-elegant table.items { width: 100%; border-collapse: collapse; margin-top: 16px; }
        .invoice-wrapper .invoice.layout-elegant table.items th { border-bottom: 2px solid #fde68a; background: transparent; color: #1e3a8a; padding: 12px 10px; font-size: calc(14px * var(--font-scale)); font-weight: bold; text-align: left; }
        .invoice-wrapper .invoice.layout-elegant table.items th.right { text-align: right; }
        .invoice-wrapper .invoice.layout-elegant table.items th.center { text-align: center; }
        .invoice-wrapper .invoice.layout-elegant table.items td { border-bottom: 1px solid #fef3c7; padding: 14px 10px; font-size: calc(14.5px * var(--font-scale)); color: #1e293b; }
        .invoice-wrapper .invoice.layout-elegant table.items tr:last-child td { border-bottom: 2px solid #fde68a; }
        .invoice-wrapper .invoice.layout-elegant table.items td.right { text-align: right; }
        .invoice-wrapper .invoice.layout-elegant table.items td.center { text-align: center; }
        
        .invoice-wrapper .invoice.layout-elegant .totals-elegant-box { background: #fffbeb; border: 1px solid #fef3c7; border-radius: 8px; padding: 20px; margin-top: 24px; max-width: 320px; margin-left: auto; }
        .invoice-wrapper .invoice.layout-elegant .totals-row { display: flex; justify-content: space-between; font-size: calc(14px * var(--font-scale)); padding: 8px 0; color: #1e3a8a; }
        .invoice-wrapper .invoice.layout-elegant .totals-row.discount { color: #92400e; font-style: italic; }
        .invoice-wrapper .invoice.layout-elegant .totals-row.total { color: #451a03; font-size: calc(17px * var(--font-scale)); font-weight: bold; border-top: none; margin-top: 4px; padding-top: 12px; }
        .invoice-wrapper .invoice.layout-elegant .totals-row.paid { color: #065f46; font-weight: 500; }
        .invoice-wrapper .invoice.layout-elegant .totals-row.balance { border-top: 1px dashed #fde68a; margin-top: 4px; padding-top: 8px; color: #475569; }
        
        .invoice-wrapper .invoice.layout-elegant .footer { text-align: center; padding-top: 30px; font-size: calc(14px * var(--font-scale)); color: #64748b; }
        .invoice-wrapper .invoice.layout-elegant .footer .thanks { font-style: italic; color: #475569; font-size: calc(15px * var(--font-scale)); margin-bottom: 12px; }
.invoice-wrapper .items-mobile { display: none; }

        @media (max-width: 640px) {
          .invoice-wrapper {
          --font-scale: ${tmplSettings.font_size === 'small' ? 0.85 : tmplSettings.font_size === 'large' ? 1.2 : 1};
            padding: 0;
            background: #fff;
          }
          .invoice-wrapper .invoice {
            max-width: 100%;
            border-radius: 0;
            box-shadow: none;
          }
          .invoice-wrapper .invoice.layout-standard {
            padding: 16px;
          }
          .invoice-wrapper .invoice.layout-standard .top-bar {
            margin: -16px -16px 16px -16px;
          }
          .invoice-wrapper .invoice.layout-standard .header {
            flex-direction: column;
            gap: 16px;
            padding: 0 0 12px 0;
          }
          .invoice-wrapper .invoice.layout-standard .logo-row {
            flex-wrap: wrap;
          }
          .invoice-wrapper .invoice.layout-standard .logo-img {
            max-width: 140px;
            max-height: 50px;
          }
          .invoice-wrapper .invoice.layout-standard .brand-name {
            font-size: calc(22px * var(--font-scale));
          }
          .invoice-wrapper .invoice.layout-standard .contact-info {
            width: 100%;
          }
          .invoice-wrapper .invoice.layout-standard .contact-info div {
            white-space: normal;
            align-items: flex-start;
            font-size: calc(13px * var(--font-scale));
          }
          .invoice-wrapper .invoice.layout-standard .divider {
            margin: 0;
          }
          .invoice-wrapper .invoice.layout-standard .meta-container {
            padding: 12px;
          }
          .invoice-wrapper .invoice.layout-standard .meta-row {
            gap: 10px;
            margin-bottom: 12px;
            font-size: calc(14px * var(--font-scale));
            flex-wrap: wrap;
          }
          .invoice-wrapper .invoice.layout-standard .meta-label {
            width: auto;
            min-width: 88px;
          }
          .invoice-wrapper .invoice.layout-standard .meta-value {
            flex: 1;
            min-width: 0;
            word-break: break-word;
          }
          .invoice-wrapper .invoice.layout-standard table.items {
            display: none;
          }
          .invoice-wrapper .invoice.layout-standard .items-mobile {
            display: block;
            margin-top: 12px;
          }
          .invoice-wrapper .invoice.layout-standard .totals {
            width: 100%;
            margin-top: 16px;
          }
          
          /* Compact Mobile */
          .invoice-wrapper .invoice.layout-compact {
            padding: 12px !important;
          }
          .invoice-wrapper .invoice.layout-compact table.items {
            display: none;
          }
          .invoice-wrapper .invoice.layout-compact .items-mobile {
            display: block;
          }
          
          /* Elegant Mobile */
          .invoice-wrapper .invoice.layout-elegant {
            padding: 24px !important;
          }
          .invoice-wrapper .invoice.layout-elegant .meta-section {
            grid-template-columns: 1fr;
            gap: 12px;
          }
          .invoice-wrapper .invoice.layout-elegant .meta-section .text-right {
            text-align: left;
          }
          .invoice-wrapper .invoice.layout-elegant table.items {
            display: none;
          }
          .invoice-wrapper .invoice.layout-elegant .items-mobile {
            display: block;
          }
          .invoice-wrapper .invoice.layout-elegant .totals-elegant-box {
            max-width: 100%;
          }
        }
        
        @media print {
          body * { visibility: hidden; }
          .invoice-wrapper, .invoice-wrapper * { visibility: visible; }
          .invoice-wrapper {
          --font-scale: ${tmplSettings.font_size === 'small' ? 0.85 : tmplSettings.font_size === 'large' ? 1.2 : 1}; position: absolute; left: 0; top: 0; width: 100%; padding: 0; margin: 0; display: flex; justify-content: flex-start; }
          .receipt-print-container { width: 100% !important; box-shadow: none !important; border: none !important; margin: 0; }
          .invoice-wrapper table.items { display: table !important; }
          .invoice-wrapper .items-mobile { display: none !important; }
        }
      `}</style>
      
      {layoutMode === 'short' && inv ? (
        <ShortReceiptLayout
          inv={inv}
          displayCompany={displayCompany}
          servedBy={servedBy}
          tmplSettings={tmplSettings}
          posSettings={posSettings}
          taxLabel={taxLabel}
        />
      ) : layoutMode === 'modern' && inv ? (
        <ModernReceiptLayout
          inv={inv}
          displayCompany={displayCompany}
          servedBy={servedBy}
          tmplSettings={tmplSettings}
          posSettings={posSettings}
          taxLabel={taxLabel}
        />
      ) : layoutMode === 'compact' ? (
        // COMPACT LAYOUT VIEW
        <div className="invoice layout-compact">
          {/* Header */}
          <div className="text-center mb-2">
            {tmplSettings.show_logo && inv?.company_logo && (
              <div className="flex justify-center mb-1">
                <img
                  src={resolveFileUrl(inv.company_logo)}
                  alt={displayCompany}
                  className="logo-img"
                />
              </div>
            )}
            <h4 className="font-bold text-base leading-none uppercase">{displayCompany}</h4>
            <SellerIdsBlock posSettings={posSettings} align="center" />
            {tmplSettings.header_text && (
              <p className="text-xs text-gray-500 mt-1 whitespace-pre-wrap leading-tight max-w-[280px] mx-auto">{tmplSettings.header_text}</p>
            )}
            {(tmplSettings.show_address || tmplSettings.show_phone || tmplSettings.show_email) && contactJoin(' | ') && (
              <p className="text-[11px] text-gray-400 mt-1">
{contactJoin(' | ')}
              </p>
            )}
          </div>

          <div className="divider-dashed" />

          {/* Meta */}
          <div className="compact-meta-row font-mono text-xs text-gray-700">
            <div>{t('receipt.invAbbr')}: {inv?.name}</div>
            <div className="text-end">{t('receipt.dtAbbr')}: {inv && new Date(inv.creation || inv.invoice_date).toLocaleDateString('en-GB')}</div>
            {tmplSettings.show_customer && (
              <div className="col-span-2">{t('receipt.custAbbr')}: {inv?.customer_name || t('receipt.walkIn')}</div>
            )}
            {tmplSettings.show_served_by && (
              <div className="col-span-2">{t('receipt.cashierAbbr')}: {servedBy || t('receipt.cashier')}</div>
            )}
          </div>

          <div className="divider-dashed" />

          {/* Table (Desktop) */}
          <table className="items hidden sm:table w-full">
            <thead>
              <tr>
                <th className="text-left">{t('receipt.item')}</th>
                <th className="text-center w-16">{t('receipt.qty')}</th>
                {tmplSettings.show_item_price !== false && <th className="text-right w-28">{t('receipt.amt')}</th>}
              </tr>
            </thead>
            <tbody>
              {inv?.items.map((item: any, idx: number) => (
                <tr key={idx}>
                  <td className="text-left py-1.5">
                    {tmplSettings.show_item_code && item.item_code ? `[${item.item_code}] ` : ''}
                    {item.item_name}
                    {item.variant_name ? ` - ${item.variant_name}` : ''}
                    {(item.is_weighted || item.batch_no || item.imei) && (
                      <div className="text-[10px] text-gray-500">
                        {item.is_weighted
                          ? `${Number(item.quantity).toFixed(3)} × `
                          : null}
                        {item.is_weighted ? (
                          <ReceiptPromoPrice item={item} show={showPromoPrint} field="rate" />
                        ) : null}
                        {item.batch_no ? `${item.is_weighted ? ' · ' : ''}${t('receipt.batch')} ${item.batch_no}` : ''}
                        {item.expiry_date ? ` · ${t('receipt.expiry')} ${item.expiry_date}` : ''}
                        {item.imei ? `${(item.is_weighted || item.batch_no) ? ' · ' : ''}IMEI ${item.imei}` : ''}
                        {item.mac_address ? ` · MAC ${item.mac_address}` : ''}
                      </div>
                    )}
                    {tmplSettings.show_item_price !== false && (
                      <div className="text-[10px] text-gray-400">
                        <ReceiptPromoPrice item={item} show={showPromoPrint} field="rate" />
                      </div>
                    )}
                  </td>
                  <td className="text-center py-1.5">{item.is_weighted ? Number(item.quantity).toFixed(3) : item.quantity}</td>
                  {tmplSettings.show_item_price !== false && (
                    <td className="text-right py-1.5 font-bold">
                      <ReceiptPromoPrice item={item} show={showPromoPrint} field="amount" />
                    </td>
                  )}
                </tr>
              ))}
            </tbody>
          </table>

          {/* Table (Mobile cards) */}
          <div className="items-mobile block sm:hidden">
            {inv?.items.map((item: any, idx: number) => (
              <div key={idx} className="border border-dashed border-gray-200 p-2 rounded mb-1 text-xs">
                <div className="font-bold text-gray-800">
                  {tmplSettings.show_item_code && item.item_code ? `[${item.item_code}] ` : ''}
                  {item.item_name} {item.variant_name ? ` - ${item.variant_name}` : ''}
                </div>
                <div className="flex justify-between mt-1 text-gray-500">
                  <span>
                    {t('receipt.qtyLabel')}: {item.is_weighted ? Number(item.quantity).toFixed(3) : item.quantity}
                    {tmplSettings.show_item_price !== false && (
                      <> @ <ReceiptPromoPrice item={item} show={showPromoPrint} field="rate" /></>
                    )}
                    {item.batch_no ? ` · ${t('receipt.batch')} ${item.batch_no}` : ''}
                    {item.expiry_date ? ` · ${t('receipt.expiry')} ${item.expiry_date}` : ''}
                    {item.imei ? ` · IMEI ${item.imei}` : ''}
                  </span>
                  {tmplSettings.show_item_price !== false && (
                    <span className="font-bold text-gray-800">
                      <ReceiptPromoPrice item={item} show={showPromoPrint} field="amount" />
                    </span>
                  )}
                </div>
              </div>
            ))}
          </div>

          <div className="divider-dashed" />

          {/* Totals */}
          {tmplSettings.show_totals !== false && (
            <div className="totals font-mono w-full text-xs">
              <div className="totals-row flex justify-between">
                <span>{t('receipt.subtotal')}:</span>
                <span>{formatMoney(subtotalDisplay)}</span>
              </div>
              {showPromoRow && (
                <div className="totals-row flex justify-between">
                  <span>{t('receipt.promotions')}:</span>
                  <span>− {formatMoney(promoTotals.promo)}</span>
                </div>
              )}
              {(!posSettings || posSettings.show_discount_in_invoice_print !== false) && (((inv?.discount_amount || 0) + (inv?.write_off_amount || 0)) > 0) && (
                <div className="totals-row flex justify-between">
                  <span>{t('receipt.discount')}:</span>
                  <span>− {formatMoney((inv?.discount_amount || 0) + (inv?.write_off_amount || 0))}</span>
                </div>
              )}
              {(!posSettings || posSettings.show_tax_in_invoice !== false) && (inv?.tax_amount || 0) > 0 && (
                <div className="totals-row flex justify-between">
                  <span>{taxLabel}:</span>
                  <span>{formatMoney(inv?.tax_amount)}</span>
                </div>
              )}
              <div className="totals-row flex justify-between font-bold text-sm text-gray-900 border-t border-dashed border-gray-300 pt-1.5 mt-1.5">
                <span>{t('receipt.total')}:</span>
                <span>{formatMoney(inv?.net_total)}</span>
              </div>
              {inv?.customer_name && inv?.customer_name !== 'Walk In' && (inv?.previous_outstanding || 0) > 0 && (
                <>
                  <div className="totals-row flex justify-between text-gray-600 mt-1">
                    <span>Previous Outstanding:</span>
                    <span>{formatMoney(inv.previous_outstanding)}</span>
                  </div>
                  <div className="totals-row flex justify-between font-bold text-gray-800">
                    <span>Total Outstanding:</span>
                    <span>{formatMoney((inv.previous_outstanding || 0) + (inv.balance_amount || 0))}</span>
                  </div>
                </>
              )}
              <div className="totals-row flex justify-between">
                <span>{t('receipt.paid')}:</span>
                <span>{formatMoney(inv?.paid_amount)}</span>
              </div>
              <div className="totals-row flex justify-between text-gray-500 border-b-0 pb-0">
                <span>{t('receipt.change')}:</span>
                <span>{formatMoney(inv?.balance_amount)}</span>
              </div>
            </div>
          )}

          {tmplSettings.show_totals !== false && (
            <>
              <div className="divider-dashed" />
              <PricesIncludeTaxNote posSettings={posSettings} />
            </>
          )}
          <FiscalBlock inv={inv} posSettings={posSettings} compact />
          <ZatcaBlock inv={inv} posSettings={posSettings} compact />

          {/* Footer */}
          <div className="text-center mt-3 text-xs text-gray-500 font-mono">
            {resolveFooter(tmplSettings.footer_text) ? (
              <div className="italic whitespace-pre-wrap leading-tight">{resolveFooter(tmplSettings.footer_text)}</div>
            ) : (
              <div className="italic">{t('receipt.thankYou')}</div>
            )}
            <div className="text-[10px] text-gray-400 mt-2">{poweredBy}</div>
          </div>
        </div>
      ) : layoutMode === 'elegant' ? (
        // ELEGANT LAYOUT VIEW
        <div className="invoice layout-elegant">
          {/* Accent border at top */}
          <div style={{ height: '4px', background: '#8a704c', margin: '-45px -50px 30px -50px' }} />

          {/* Header */}
          <div className="text-center mb-4">
            {tmplSettings.show_logo && inv?.company_logo && (
              <div className="flex justify-center mb-3">
                <img
                  src={resolveFileUrl(inv.company_logo)}
                  alt={displayCompany}
                  className="logo-img"
                />
              </div>
            )}
            <h4 className="elegant-title">{displayCompany}</h4>
            <div className="elegant-line" />
            <div className="flex justify-center"><SellerIdsBlock posSettings={posSettings} align="center" /></div>
            {tmplSettings.header_text && (
              <p className="text-sm text-gray-600 italic whitespace-pre-wrap leading-relaxed max-w-[400px] mx-auto mt-2 font-serif">{tmplSettings.header_text}</p>
            )}
            {(tmplSettings.show_address || tmplSettings.show_phone || tmplSettings.show_email) && contactJoin('  •  ') && (
              <p className="text-xs text-gray-500 mt-2 font-serif">
{contactJoin('  •  ')}
              </p>
            )}
          </div>

          <div className="divider-elegant-double" />

          {/* Metadata */}
          <div className="meta-section font-serif">
            <div className="text-left">
              <div className="text-[10px] uppercase tracking-wider text-gray-400 mb-1">{t('receipt.billedTo')}</div>
              <div className="font-bold text-gray-800 text-base">{tmplSettings.show_customer ? (inv?.customer_name || t('receipt.walkIn')) : t('receipt.walkInCustomer')}</div>
            </div>
            <div className="text-right">
              <div className="text-[10px] uppercase tracking-wider text-gray-400 mb-1">{t('receipt.invoiceDetails')}</div>
              <div className="font-bold text-gray-800 text-base">{inv?.name}</div>
              <div className="text-xs text-gray-500 mt-0.5">
                {inv && new Date(inv.creation || inv.invoice_date).toLocaleDateString('en-GB', { day: '2-digit', month: '2-digit', year: 'numeric' })}
              </div>
              {tmplSettings.show_served_by && (
                <div className="text-xs text-gray-500 italic mt-0.5">{t('receipt.by')}: {servedBy || t('receipt.cashier')}</div>
              )}
            </div>
          </div>

          <div className="divider-elegant-single" />

          {/* Items Table (Desktop) */}
          <table className="items hidden sm:table w-full font-serif">
            <thead>
              <tr>
                <th className="text-left">{t('receipt.description')}</th>
                <th className="center w-16">{t('receipt.qty')}</th>
                {tmplSettings.show_item_price !== false && <th className="right w-28">{t('receipt.unitPrice')}</th>}
                {tmplSettings.show_item_price !== false && <th className="right w-28">{t('receipt.amount')}</th>}
              </tr>
            </thead>
            <tbody>
              {inv?.items.map((item: any, idx: number) => (
                <tr key={idx}>
                  <td className="text-left py-3">
                    {tmplSettings.show_item_code && item.item_code ? `[${item.item_code}] ` : ''}
                    <span className="font-bold text-gray-800">{item.item_name}</span>
                    {item.variant_name ? ` - ${item.variant_name}` : ''}
                  </td>
                  <td className="center py-3">{item.is_weighted ? Number(item.quantity).toFixed(3) : item.quantity}</td>
                  {tmplSettings.show_item_price !== false && (
                    <td className="right py-3 italic text-gray-500">
                      <ReceiptPromoPrice item={item} show={showPromoPrint} field="rate" />
                    </td>
                  )}
                  {tmplSettings.show_item_price !== false && (
                    <td className="right py-3 font-semibold text-gray-800">
                      <ReceiptPromoPrice item={item} show={showPromoPrint} field="amount" />
                    </td>
                  )}
                </tr>
              ))}
            </tbody>
          </table>

          {/* Items Table (Mobile) */}
          <div className="items-mobile block sm:hidden font-serif">
            {inv?.items.map((item: any, idx: number) => (
              <div key={idx} className="border-b border-gray-100 py-3 text-sm">
                <div className="font-bold text-gray-800">
                  {tmplSettings.show_item_code && item.item_code ? `[${item.item_code}] ` : ''}
                  {item.item_name} {item.variant_name ? ` - ${item.variant_name}` : ''}
                </div>
                <div className="flex justify-between mt-1 text-gray-500 text-xs">
                  <span>
                    {t('receipt.qtyLabel')}: {item.is_weighted ? Number(item.quantity).toFixed(3) : item.quantity}
                    {tmplSettings.show_item_price !== false && (
                      <> @ <ReceiptPromoPrice item={item} show={showPromoPrint} field="rate" /></>
                    )}
                    {item.batch_no ? ` · ${t('receipt.batch')} ${item.batch_no}` : ''}
                    {item.expiry_date ? ` · ${t('receipt.expiry')} ${item.expiry_date}` : ''}
                    {item.imei ? ` · IMEI ${item.imei}` : ''}
                  </span>
                  {tmplSettings.show_item_price !== false && (
                    <span className="font-bold text-gray-800">
                      <ReceiptPromoPrice item={item} show={showPromoPrint} field="amount" />
                    </span>
                  )}
                </div>
              </div>
            ))}
          </div>

          <div className="divider-elegant-single" />

          {/* Totals Frame */}
          {tmplSettings.show_totals !== false && (
            <div className="totals-elegant-box font-serif">
              <div className="totals-row flex justify-between">
                <span>{t('receipt.subtotal')}:</span>
                <span>{formatMoney(subtotalDisplay)}</span>
              </div>
              {showPromoRow && (
                <div className="totals-row flex justify-between italic text-rose-800">
                  <span>{t('receipt.promotions')}:</span>
                  <span>− {formatMoney(promoTotals.promo)}</span>
                </div>
              )}
              {(!posSettings || posSettings.show_discount_in_invoice_print !== false) && (((inv?.discount_amount || 0) + (inv?.write_off_amount || 0)) > 0) && (
                <div className="totals-row flex justify-between italic text-amber-800">
                  <span>{t('receipt.discount')}:</span>
                  <span>− {formatMoney((inv?.discount_amount || 0) + (inv?.write_off_amount || 0))}</span>
                </div>
              )}
              {(!posSettings || posSettings.show_tax_in_invoice !== false) && (inv?.tax_amount || 0) > 0 && (
                <div className="totals-row flex justify-between">
                  <span>{taxLabel}:</span>
                  <span>{formatMoney(inv?.tax_amount)}</span>
                </div>
              )}
              <div className="totals-row total flex justify-between border-t border-amber-200/50 pt-2 mt-2 font-bold text-base">
                <span>{t('receipt.totalAmount')}:</span>
                <span>{formatMoney(inv?.net_total)}</span>
              </div>
              {inv?.customer_name && inv?.customer_name !== 'Walk In' && (inv?.previous_outstanding || 0) > 0 && (
                <>
                  <div className="totals-row flex justify-between text-gray-500 mt-1">
                    <span>Previous Outstanding:</span>
                    <span>{formatMoney(inv.previous_outstanding)}</span>
                  </div>
                  <div className="totals-row flex justify-between font-bold text-gray-800">
                    <span>Total Outstanding:</span>
                    <span>{formatMoney((inv.previous_outstanding || 0) + (inv.balance_amount || 0))}</span>
                  </div>
                </>
              )}
              <div className="totals-row flex justify-between text-emerald-800 font-medium">
                <span>{t('receipt.amountPaid')}:</span>
                <span>{formatMoney(inv?.paid_amount)}</span>
              </div>
              <div className="totals-row flex justify-between text-gray-500 border-b-0 pb-0 pt-1 border-t border-dashed border-amber-100">
                <span>{t('receipt.balance')}:</span>
                <span>{formatMoney(inv?.balance_amount)}</span>
              </div>
            </div>
          )}

          <div className="divider-elegant-double" />

          {/* Footer */}
          <div className="text-center mt-4 text-sm text-gray-500 font-serif">
            {tmplSettings.show_totals !== false && <PricesIncludeTaxNote posSettings={posSettings} />}
            <FiscalBlock inv={inv} posSettings={posSettings} />
            <ZatcaBlock inv={inv} posSettings={posSettings} />
            {resolveFooter(tmplSettings.footer_text) ? (
              <div className="thanks italic whitespace-pre-wrap leading-relaxed">{resolveFooter(tmplSettings.footer_text)}</div>
            ) : (
              <div className="thanks italic">{t('receipt.thankYouPatronage')}</div>
            )}
            <div className="text-xs text-gray-400 mt-3 font-semibold">
              <span>❈ {poweredBy} ❈</span>
            </div>
          </div>
        </div>
      ) : (
        // STANDARD LAYOUT VIEW (DEFAULT)
        <div className="invoice layout-standard">
          <div className="top-bar"></div>
          <div className="header">
            <div>
              <div className="logo-row">
                {tmplSettings.show_logo && inv?.company_logo && (
                  <img
                    src={resolveFileUrl(inv.company_logo)}
                    alt={displayCompany}
                    className="logo-img"
                  />
                )}
                <div className="brand-name">
                  {displayCompany}
                  <SellerIdsBlock posSettings={posSettings} align="center" />
                  {tmplSettings.header_text && (
                    <div className="text-xs text-gray-500 font-normal mt-1.5 whitespace-pre-wrap leading-tight max-w-[280px]" style={{ textTransform: 'none' }}>{tmplSettings.header_text}</div>
                  )}
                </div>
              </div>
            </div>
            <div className="contact-info">
              {tmplSettings.show_phone && inv?.company_phone && <div><svg className="icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M22 16.92v3a2 2 0 0 1-2.18 2 19.79 19.79 0 0 1-8.63-3.07 19.5 19.5 0 0 1-6-6 19.79 19.79 0 0 1-3.07-8.67A2 2 0 0 1 4.11 2h3a2 2 0 0 1 2 1.72c.12.81.32 1.6.61 2.36a2 2 0 0 1-.45 2.11L8.09 9.91a16 16 0 0 0 6 6l1.72-1.72a2 2 0 0 1 2.11-.45c.76.29 1.55.49 2.36.61A2 2 0 0 1 22 16.92z"/></svg> {inv?.company_phone}</div>}
              {tmplSettings.show_email && (inv?.company_email) && <div><svg className="icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M4 4h16v16H4z" opacity="0"/><path d="M22 6l-10 7L2 6"/><rect x="2" y="4" width="20" height="16" rx="2"/></svg> {inv?.company_email}</div>}
              {tmplSettings.show_address && (inv?.company_address) && <div><svg className="icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M21 10c0 7-9 13-9 13s-9-6-9-13a9 9 0 0 1 18 0z"/><circle cx="12" cy="10" r="3"/></svg> {inv?.company_address}</div>}
            </div>
          </div>

          <hr className="divider" />

          <div className="meta-container">
            <div className="meta-row">
              <div className="meta-icon"><svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M9 3H4a1 1 0 0 0-1 1v16a1 1 0 0 0 1 1h16a1 1 0 0 0 1-1V9l-6-6z"/><path d="M13 3v6h6M8 13h8M8 17h8M8 9h2"/></svg></div>
              <div className="meta-label">{t('receipt.invoiceNo')}</div>
              <div className="meta-sep">:</div>
              <div className="meta-value">{inv?.name}</div>
            </div>
            <div className="meta-row">
              <div className="meta-icon"><svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><rect x="3" y="4" width="18" height="18" rx="2"/><path d="M16 2v4M8 2v4M3 10h18"/></svg></div>
              <div className="meta-label">{t('receipt.invoiceDateTime')}</div>
              <div className="meta-sep">:</div>
              <div className="meta-value">{inv && (new Date(inv.creation || inv.invoice_date).toLocaleDateString('en-GB', { day: '2-digit', month: '2-digit', year: 'numeric' }) + ', ' + new Date(inv.creation || inv.invoice_date).toLocaleTimeString('en-GB', { hour12: false, hour: '2-digit', minute: '2-digit', second: '2-digit' }))}</div>
            </div>
            {tmplSettings.show_customer && (
              <div className="meta-row">
                <div className="meta-icon"><svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><circle cx="12" cy="8" r="4"/><path d="M4 21v-1a8 8 0 0 1 16 0v1"/></svg></div>
                <div className="meta-label">{t('receipt.customer')}</div>
                <div className="meta-sep">:</div>
                <div className="meta-value">{inv?.customer_name || t('receipt.walkIn')}</div>
              </div>
            )}
            {tmplSettings.show_served_by && (
              <div className="meta-row">
                <div className="meta-icon"><svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><circle cx="12" cy="7" r="4"/><path d="M5.5 21a7 7 0 0 1 13 0"/><path d="M2 9l3 3-3 3"/></svg></div>
                <div className="meta-label">{t('receipt.servedBy')}</div>
                <div className="meta-sep">:</div>
                <div className="meta-value">{servedBy || t('receipt.cashier')}</div>
              </div>
            )}
          </div>

          <table className="items">
            <thead>
              <tr>
                <th className="num-col">#</th>
                <th>{t('receipt.itemDescription')}</th>
                {tmplSettings.show_item_price !== false && <th className="right">{t('receipt.unitPrice')}</th>}
                <th className="right">{t('receipt.qty')}</th>
                {tmplSettings.show_item_price !== false && <th className="right">{t('receipt.amount')}</th>}
              </tr>
            </thead>
            <tbody>
              {inv?.items.map((item: any, idx: number) => (
                <tr key={idx}>
                  <td className="num-col">{idx + 1}</td>
                  <td>
                    {tmplSettings.show_item_code && item.item_code ? `[${item.item_code}] ` : ''}
                    {item.item_name}
                    {item.variant_name ? ` - ${item.variant_name}` : ''}
                  </td>
                  {tmplSettings.show_item_price !== false && (
                    <td className="right">
                      <ReceiptPromoPrice item={item} show={showPromoPrint} field="rate" />
                    </td>
                  )}
                  <td className="right">{item.is_weighted ? Number(item.quantity).toFixed(3) : item.quantity}</td>
                  {tmplSettings.show_item_price !== false && (
                    <td className="right amount">
                      <ReceiptPromoPrice item={item} show={showPromoPrint} field="amount" />
                    </td>
                  )}
                </tr>
              ))}
            </tbody>
          </table>

          <div className="items-mobile">
            {inv?.items.map((item: any, idx: number) => (
              <div key={idx} className="item-card">
                <div className="item-card-title">
                  #{idx + 1} {tmplSettings.show_item_code && item.item_code ? `[${item.item_code}] ` : ''}
                  {item.item_name}
                  {item.variant_name ? ` - ${item.variant_name}` : ''}
                </div>
                <div className="item-card-row">
                  <span>{t('receipt.qtyLabel')}</span>
                  <span>{item.is_weighted ? Number(item.quantity).toFixed(3) : item.quantity}{item.batch_no ? ` · ${item.batch_no}` : ''}</span>
                </div>
                {tmplSettings.show_item_price !== false && (
                  <>
                    <div className="item-card-row">
                      <span>{t('receipt.rate')}</span>
                      <span><ReceiptPromoPrice item={item} show={showPromoPrint} field="rate" /></span>
                    </div>
                    <div className="item-card-row">
                      <span>{t('receipt.amount')}</span>
                      <span><ReceiptPromoPrice item={item} show={showPromoPrint} field="amount" /></span>
                    </div>
                  </>
                )}
              </div>
            ))}
          </div>

          {tmplSettings.show_totals !== false && (
            <div className="totals">
              <div className="totals-row"><span>{t('receipt.subtotal')}</span><span>{formatMoney(subtotalDisplay)}</span></div>
              {showPromoRow && (
                <div className="totals-row">
                  <span>{t('receipt.promotions')}</span>
                  <span>− {formatMoney(promoTotals.promo)}</span>
                </div>
              )}
              {(!posSettings || posSettings.show_discount_in_invoice_print !== false) && (((inv?.discount_amount || 0) + (inv?.write_off_amount || 0)) > 0) && (
                <div className="totals-row">
                  <span>{t('receipt.discount')}</span>
                  <span>− {formatMoney((inv?.discount_amount || 0) + (inv?.write_off_amount || 0))}</span>
                </div>
              )}
              {(!posSettings || posSettings.show_tax_in_invoice !== false) && (inv?.tax_amount || 0) > 0 && (
                <div className="totals-row">
                  <span>{taxLabel}</span>
                  <span>{formatMoney(inv?.tax_amount)}</span>
                </div>
              )}
              <div className="totals-row total totals-highlight"><span>{t('receipt.totalAmount')}</span><span>{formatMoney(inv?.net_total)}</span></div>
              {inv?.customer_name && inv?.customer_name !== 'Walk In' && (inv?.previous_outstanding || 0) > 0 && (
                <>
                  <div className="totals-row text-gray-600">
                    <span>Previous Outstanding</span>
                    <span>{formatMoney(inv.previous_outstanding)}</span>
                  </div>
                  <div className="totals-row font-bold text-gray-800">
                    <span>Total Outstanding</span>
                    <span>{formatMoney((inv.previous_outstanding || 0) + (inv.balance_amount || 0))}</span>
                  </div>
                </>
              )}
              <div className="paid-box"><span>{t('receipt.paidAmount')}</span><span>{formatMoney(inv?.paid_amount)}</span></div>
              
              <div className={`status-box ${(inv?.balance_amount || 0) > 0 ? 'unpaid' : ''}`}>
                <span className="check">{(inv?.balance_amount || 0) > 0 ? '!' : '✓'}</span>
                {(inv?.balance_amount || 0) > 0
                  ? t('receipt.balanceDueLabel', { amount: formatMoney(inv?.balance_amount) })
                  : t('receipt.paymentStatusPaid')}
              </div>
            </div>
          )}

          <div className="footer">
            {tmplSettings.show_totals !== false && <PricesIncludeTaxNote posSettings={posSettings} />}
            <FiscalBlock inv={inv} posSettings={posSettings} />
            <ZatcaBlock inv={inv} posSettings={posSettings} />

                        {resolveFooter(tmplSettings.footer_text) ? (
              <div className="thanks whitespace-pre-wrap italic leading-relaxed" style={{ textTransform: 'none' }}>
                {resolveFooter(tmplSettings.footer_text)}
              </div>
            ) : (
              <>
                <div className="thanks"><span className="star">★</span> {t('receipt.thankYouShopping')} <span className="star">★</span></div>
                <div className="sub">{t('receipt.appreciateTrust')}</div>
              </>
            )}
            <hr className="footer-line" />
            <div className="powered">{poweredBy}. <span>{t('receipt.smartPos')}</span></div>
          </div>
        </div>
      )}
    </div>
  );


  // Template designer passes previewSettings — embed bare receipt (no modal chrome).
  // Post-sale offline receipts also pass previewInvoice but must use the full modal.
  if (previewInvoice && previewSettings) {
    return renderReceipt(visualReceiptRef);
  }

  const receiptReady = Boolean(inv) || Boolean(previewInvoice);
  const receiptLoading = !previewInvoice && isLoading;

  return (
    <div className="fixed inset-0 bg-black/50 flex items-end sm:items-center justify-center z-50 p-0 sm:p-4">

      <div className="bg-white rounded-t-xl sm:rounded-xl shadow-xl w-full max-w-2xl max-h-[95vh] sm:max-h-[90vh] overflow-hidden flex flex-col">
        <div className="flex items-center justify-between p-4 border-b border-gray-100">
          <h3 className="font-semibold text-gray-900">{t('receipt.title')}</h3>
          <button onClick={onClose} className="p-1.5 hover:bg-gray-100 rounded-lg"><X className="w-5 h-5 text-gray-400" /></button>
        </div>

        {showSuccess && (
          <div className="mx-4 mt-4 bg-emerald-50 text-emerald-700 px-4 py-2 rounded-lg flex items-center gap-2 text-sm">
            <Check className="w-4 h-4" /> {t('receipt.saleCompleted')}
          </div>
        )}

        {printError && (
          <div className="mx-4 mt-3 bg-amber-50 text-amber-800 px-4 py-2 rounded-lg flex items-center gap-2 text-xs">
            <AlertTriangle className="w-4 h-4 shrink-0" />
            <span>{printError}</span>
          </div>
        )}

        <div className="flex-1 overflow-y-auto p-2 sm:p-4">
          {receiptLoading ? (
            <div className="flex items-center justify-center py-12"><Loader2 className="w-6 h-6 animate-spin text-gray-400" /></div>
          ) : !receiptReady ? (
            <div className="text-center py-12 text-gray-400">{t('receipt.loadFailed')}</div>
          ) : (
            <div className="w-full">
              <div className="w-full bg-gray-100 sm:rounded-lg sm:border sm:border-gray-200">
                {renderReceipt(visualReceiptRef)}
              </div>
            </div>
          )}
        </div>

        <div className="p-3 sm:p-4 border-t border-gray-100 flex flex-col sm:flex-row gap-2 sm:gap-3">
          <button
            onClick={handlePrint}
            disabled={printing || receiptLoading || !receiptReady}
            className="flex-1 py-2 bg-emerald-600 text-white rounded-lg font-medium hover:bg-emerald-700 transition-colors flex items-center justify-center gap-2"
          >
            {printing ? <Loader2 className="w-4 h-4 animate-spin" /> : <Printer className="w-4 h-4" />}
            {printing ? t('receipt.printing') : isPrinterConfigured ? t('receipt.printThermal') : t('receipt.print')}
          </button>
          <button onClick={handleDownloadImage} className="flex-1 py-2 bg-blue-600 text-white rounded-lg font-medium hover:bg-blue-700 transition-colors flex items-center justify-center gap-2">
            <Download className="w-4 h-4" /> {t('receipt.downloadImage')}
          </button>
          <button
            onClick={handleShareWhatsApp}
            disabled={sharing || receiptLoading || !receiptReady}
            className="flex-1 py-2 bg-emerald-700 text-white rounded-lg font-medium hover:bg-emerald-800 transition-colors flex items-center justify-center gap-2"
          >
            {sharing ? <Loader2 className="w-4 h-4 animate-spin" /> : <Share2 className="w-4 h-4" />}
            {sharing ? t('receipt.sharing') : t('receipt.shareWhatsApp')}
          </button>
        </div>
        <p className="text-center text-xs text-gray-400 pb-3">
          {isPrinterConfigured
            ? settings.connectionType === "bluetooth"
              ? t('receipt.directPrintBluetooth')
              : t('receipt.directPrintHelper', { name: settings.windowsPrinterName })
            : t('receipt.configureThermal')}
        </p>
      </div>
    </div>
  );
}
